import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, CreditCard, CheckCircle, ArrowLeft, ShieldCheck, Calendar, Clock, User } from 'lucide-react';
// Assuming you have an API call to create the booking in backend
// import { studentBookingApi } from '../../services/counselorApi'; 

export default function CheckoutPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
            <h2 className="text-xl font-bold">Invalid Session</h2>
            <button onClick={() => navigate(-1)} className="text-indigo-600 underline mt-2">Go Back</button>
        </div>
      </div>
    );
  }

  const { counselorName, planName, amount, date, timeSlot } = state;
  const formattedDate = new Date(date).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const handlePayment = async () => {
    setProcessing(true);
    
    // Simulate Payment Delay
    setTimeout(async () => {
      try {
        // Here you would call your backend API to create the booking
        // const res = await studentBookingApi.createBooking({ ...state });
        
        alert("Payment Successful! Booking Confirmed.");
        navigate('/student/dashboard'); // Redirect to dashboard after success
      } catch (error) {
        alert("Payment Failed. Please try again.");
      } finally {
        setProcessing(false);
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-500 hover:text-gray-800 mb-6 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2"/> Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Secure Checkout</h1>

        <div className="grid md:grid-cols-3 gap-8">
            {/* Left: Payment Details */}
            <div className="md:col-span-2 space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                        <CreditCard className="w-5 h-5 mr-2 text-indigo-600"/> Payment Method
                    </h2>
                    
                    <div className="space-y-3">
                        <div className="flex items-center p-4 border-2 border-indigo-600 bg-indigo-50/50 rounded-lg cursor-pointer">
                            <div className="w-5 h-5 rounded-full border-2 border-indigo-600 flex items-center justify-center mr-3">
                                <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full"></div>
                            </div>
                            <span className="font-medium text-gray-800">Credit / Debit Card</span>
                            <div className="ml-auto flex gap-2">
                                <span className="text-xs bg-white border border-gray-200 px-2 py-1 rounded">VISA</span>
                                <span className="text-xs bg-white border border-gray-200 px-2 py-1 rounded">MasterCard</span>
                            </div>
                        </div>
                        <div className="flex items-center p-4 border border-gray-200 rounded-lg opacity-60 cursor-not-allowed">
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                            <span className="text-gray-500">UPI / Net Banking (Coming Soon)</span>
                        </div>
                    </div>

                    <div className="mt-6">
                         <label className="block text-sm font-bold text-gray-700 mb-2">Card Information</label>
                         <div className="p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-400 select-none">
                            •••• •••• •••• 4242
                         </div>
                    </div>
                </div>

                <div className="flex items-center justify-center text-sm text-gray-500 gap-2">
                    <ShieldCheck className="w-4 h-4 text-green-600"/> Payments are secure and encrypted
                </div>
            </div>

            {/* Right: Order Summary */}
            <div className="md:col-span-1">
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 sticky top-24">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>
                    
                    <div className="space-y-4 mb-6 border-b border-gray-100 pb-6">
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Counselor</p>
                            <div className="flex items-center gap-2">
                                <div className="bg-indigo-100 p-1.5 rounded-full"><User className="w-4 h-4 text-indigo-600"/></div>
                                <span className="font-medium text-gray-800">{counselorName}</span>
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Session Date</p>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400"/>
                                <span className="text-sm text-gray-700">{formattedDate}</span>
                            </div>
                        </div>

                        <div>
                             <p className="text-xs text-gray-500 uppercase font-bold mb-1">Time Slot</p>
                             <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400"/>
                                <span className="text-sm text-gray-700">{timeSlot}</span>
                            </div>
                        </div>

                        <div>
                             <p className="text-xs text-gray-500 uppercase font-bold mb-1">Plan</p>
                             <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">{planName}</span>
                        </div>
                    </div>

                    <div className="flex justify-between items-center mb-6">
                        <span className="font-bold text-gray-800">Total Amount</span>
                        <span className="text-2xl font-bold text-green-600">₹{amount}</span>
                    </div>

                    <button 
                        onClick={handlePayment}
                        disabled={processing}
                        className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition flex items-center justify-center gap-2 ${
                            processing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 hover:-translate-y-0.5'
                        }`}
                    >
                        {processing ? 'Processing...' : `Pay ₹${amount}`}
                    </button>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}